import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaCode } from 'react-icons/fa';

/**
 * Error boundary that catches ProseMirror render errors thrown by DocxEditor
 * (e.g. "TextSelection endpoint not pointing into a node with inline content",
 * "Position X out of range") and auto-recovers by remounting the editor with a
 * fresh key. These are bugs in the paged layout bridge of @eigenpal/docx-editor-react.
 */
class DocxEditorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, recoveryKey: 0 };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(err) {
		console.warn('[DocxEditor] render error caught, auto-recovering:', err.message);
		// Reset after one tick so React finishes the error-render cycle before remounting
		setTimeout(() => {
			this.setState((s) => ({ hasError: false, recoveryKey: s.recoveryKey + 1 }));
		}, 100);
	}

	render() {
		if (this.state.hasError) return null;
		// Changing the key forces a full remount of the DocxEditor, clearing the bad PM state
		return React.cloneElement(
			React.Children.only(this.props.children),
			{ key: `docx-editor-${this.state.recoveryKey}` }
		);
	}
}
import { DocxEditor, createEmptyDocument } from '@guillermorecoba/docx-editor-react';
import editorEs from './editorEs';
import '@guillermorecoba/docx-editor-react/styles.css';
import './ContractTemplateEditor.css';
import { toast } from '../../toasts';
import { useNavigate, useParams } from 'react-router-dom';
import contractTemplateService from '../../services/contractTemplate.service';
import { getError, arrayBufferToBase64, base64ToArrayBuffer } from '../../utils';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import { Button, ConfirmDialog, Pill, Select } from '../../components/EntityList';
import VariablePickerModal from './VariablePickerModal';

const CATEGORIES = ['alquiler', 'venta', 'activo'];
const CATEGORY_LABELS = { alquiler: 'Alquiler', venta: 'Venta', activo: 'Activo' };

const AUTO_SAVE_INTERVAL = 15000; // 15 seconds

// Full-viewport style — stable reference outside component so it never triggers prop-change effects
const EDITOR_STYLE = { height: '100vh' };

export default function ContractTemplateEditor() {
	const { id } = useParams();
	const navigate = useNavigate();

	const [name, setName] = useState('');
	const [category, setCategory] = useState('alquiler');
	// undefined = not yet loaded; null = no content (blank doc); ArrayBuffer = existing content
	const [initialBuffer, setInitialBuffer] = useState(undefined);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [lastDraftSave, setLastDraftSave] = useState(null);
	const [isDirty, setIsDirty] = useState(false);
	const [showVariables, setShowVariables] = useState(false);
	const [showExitConfirm, setShowExitConfirm] = useState(false);

	const editorRef = useRef(null);
	const emptyDocRef = useRef(null); // lazily-created blank Document for templates with no content yet
	const readyRef = useRef(false); // true after editor view is ready; guards against false-dirty on load
	const isDirtyRef = useRef(false); // mirrors isDirty for the autosave interval tick (avoids stale closures)
	const savingRef = useRef(false); // mirrors saving to avoid a tick racing with a manual full save
	const savedSelectionRef = useRef(null); // cursor/range captured when the variable modal opens

	// Load template — id is always a real DB id (setup page creates the record before navigating here)
	useEffect(() => {
		const load = async () => {
			try {
				const res = await contractTemplateService.getDetails(id);
				const t = res.data;
				setName(t.name);
				setCategory(t.category);
				setInitialBuffer(t.content ? base64ToArrayBuffer(t.content) : null);
				if (t.lastDraftSave) setLastDraftSave(new Date(t.lastDraftSave));
			} catch (err) {
				toast.error(getError(err));
				setInitialBuffer(null);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [id]);

	// Saves the current draft; used by the 15s autosave interval
	const saveDraftNow = useCallback(async () => {
		try {
			const ab = await editorRef.current?.save();
			if (!ab) return;
			await contractTemplateService.saveDraft(id, arrayBufferToBase64(ab));
			setLastDraftSave(new Date());
		} catch {
			// Silent fail for auto-save
		}
	}, [id]);

	// onChange fires on document edits; readyRef guards against false-dirty on initial load
	const handleDocChange = useCallback(() => {
		if (!readyRef.current) return;
		setIsDirty(true);
	}, []);

	// Stable callback — must not change identity between renders.
	// DocxEditor watches this in a useEffect; new reference on every render corrupts paged layout.
	const handleEditorViewReady = useCallback(() => {
		readyRef.current = true;
	}, []);

	// Keep refs in sync so the interval tick below always reads the latest state
	useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);
	useEffect(() => { savingRef.current = saving; }, [saving]);

	// Autosave every 15s, but only while the tab is visible and there are unsaved changes
	useEffect(() => {
		const tick = () => {
			if (document.visibilityState !== 'visible') return;
			if (!readyRef.current) return;
			if (savingRef.current) return;
			if (!isDirtyRef.current) return;
			saveDraftNow();
		};
		const interval = setInterval(tick, AUTO_SAVE_INTERVAL);
		return () => clearInterval(interval);
	}, [saveDraftNow]);

	// docProps: existing content → documentBuffer; no content → blank Document object.
	// (documentBuffer=null shows "No document loaded" placeholder, so we use document prop instead.)
	// useMemo keeps the prop values referentially stable — must be above any early return (Rules of Hooks).
	if (!emptyDocRef.current) emptyDocRef.current = createEmptyDocument();
	const docProps = useMemo(
		() => initialBuffer ? { documentBuffer: initialBuffer } : { document: emptyDocRef.current },
		[initialBuffer] // emptyDocRef.current is stable by construction
	);

	// Captures the current cursor/range before the modal steals DOM focus — the
	// live selection is gone by the time the user picks a field, so insertVariable
	// below replays this saved position instead of reading state.selection.
	const openVariableModal = () => {
		const editorHandle = editorRef.current?.getEditorRef();
		const state = editorHandle?.getState();
		if (state) savedSelectionRef.current = { from: state.selection.from, to: state.selection.to };
		setShowVariables(true);
	};

	// Insert {{variable}} token at the position captured by openVariableModal.
	// Uses PagedEditorRef.getState() + dispatch() (full document state) to avoid
	// "Position out of range" errors from page-level PM fragment views. Positions
	// are re-clamped against the current doc size as a defensive measure.
	const insertVariable = (token) => {
		const editorHandle = editorRef.current?.getEditorRef();
		if (!editorHandle) return;
		try {
			const state = editorHandle.getState();
			if (!state) return;
			const size = state.doc.content.size;
			const saved = savedSelectionRef.current;
			const from = saved ? Math.min(saved.from, size) : state.selection.from;
			const to = saved ? Math.min(saved.to, size) : state.selection.to;
			editorHandle.dispatch(state.tr.insertText(token, from, to));
		} catch {
			try {
				const state = editorHandle.getState();
				if (state) editorHandle.dispatch(state.tr.insertText(token, 1, 1));
			} catch { /* ignore */ }
		}
		editorHandle.focus();
		setIsDirty(true);
	};

	const handleSave = async () => {
		if (!name.trim()) {
			toast.error('El nombre es requerido');
			return;
		}
		try {
			setSaving(true);
			const ab = await editorRef.current?.save();
			const content = ab ? arrayBufferToBase64(ab) : null;
			await contractTemplateService.edit(id, { name, category, content });
			toast.success('Plantilla guardada');
			setIsDirty(false);
			setLastDraftSave(null);
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setSaving(false);
		}
	};

	const handleClose = () => {
		if (isDirty) {
			setShowExitConfirm(true);
			return;
		}
		navigate('/AdminScreen/Config/herramientas/plantillas');
	};

	if (loading || initialBuffer === undefined) return <DetailPageLoader entityLabel='editor' />;

	// Single entry point for the variable picker, rendered in the editor toolbar (toolbarExtra).
	// onMouseDown + preventDefault keeps focus in the editor long enough for
	// openVariableModal to read a valid selection before the modal steals it.
	const variableButtons = (
		<div className='d-flex align-items-center' style={{ padding: '0 4px' }}>
			<Button
				size='sm'
				variant='default'
				icon={<FaCode />}
				className='variable-insert-btn'
				onMouseDown={(e) => {
					e.preventDefault();
					openVariableModal();
				}}
			>
				Insertar variable
			</Button>
		</div>
	);

	// Right-side title bar controls: draft badge, category, save, close
	const titleBarRight = () => (
		<div className='d-flex align-items-center gap-2'>
			{lastDraftSave && (
				<Pill tone='warn'>Borrador {lastDraftSave.toLocaleTimeString('es-UY')}</Pill>
			)}
			{isDirty && !lastDraftSave && <Pill tone='neutral'>Sin guardar</Pill>}
			<Select
				options={CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))}
				value={category}
				onChange={(e) => { setCategory(e.target.value); setIsDirty(true); }}
			/>
			<Button variant='primary' size='sm' onClick={handleSave} disabled={saving}>
				{saving ? 'Guardando...' : 'Guardar'}
			</Button>
			<Button variant='default' size='sm' onClick={handleClose}>
				Cerrar
			</Button>
		</div>
	);

	return (
		<div className='contract-template-editor'>
			<DocxEditorBoundary>
				<DocxEditor
					ref={editorRef}
					{...docProps}
					mode='editing'
					documentName={name}
					onDocumentNameChange={(n) => { setName(n); setIsDirty(true); }}
					renderTitleBarRight={titleBarRight}
					toolbarExtra={variableButtons}
					i18n={editorEs}
					onChange={handleDocChange}
					onEditorViewReady={handleEditorViewReady}
					onError={(err) => console.warn('DocxEditor error:', err)}
					style={EDITOR_STYLE}
				/>
			</DocxEditorBoundary>
			<VariablePickerModal
				show={showVariables}
				onClose={() => setShowVariables(false)}
				category={category}
				onInsert={insertVariable}
			/>
			<ConfirmDialog
				show={showExitConfirm}
				onClose={() => setShowExitConfirm(false)}
				onConfirm={() => navigate('/AdminScreen/Config/herramientas/plantillas')}
				title='Cambios sin guardar'
				description='Hay cambios sin guardar. ¿Salir de todas formas?'
				confirmCaption='Salir sin guardar'
				cancelCaption='Cancelar'
				tone='danger'
			/>
		</div>
	);
}
